import pako from 'pako';

/** Raised by the budgeted inflate's own `onData` to abandon the stream; never leaves this module. */
class OverBudgetError extends Error {}

/** pako's success status; anything else is a corrupt or truncated member. */
const INFLATE_OK = 0;

/**
 * Inflates a gzip member only while its output stays within `maxOutputBytes`, answering
 * `'over-budget'` the moment it would not. A member's inflated size is not bounded by its
 * compressed size, so the output is tallied chunk by chunk and the stream abandoned mid-inflate:
 * a caller that can only measure the compressed bytes cannot otherwise stop the allocation that
 * breaches its cap.
 *
 * Throws on malformed or truncated input.
 */
export function inflateWithinBudget(
    arrayBuffer: ArrayBuffer,
    maxOutputBytes: number
): ArrayBuffer | 'over-budget' {
    const inflater = new pako.Inflate();
    const chunks: Uint8Array[] = [];
    let produced = 0;
    let endStatus: number | undefined;

    // Replaces pako's own accumulation, so a chunk past the budget is never retained.
    inflater.onData = (chunk): void => {
        const bytes = chunk as Uint8Array;
        produced += bytes.length;
        if (produced > maxOutputBytes) {
            throw new OverBudgetError();
        }
        chunks.push(bytes);
    };
    inflater.onEnd = (status): void => {
        endStatus = status;
    };

    try {
        inflater.push(new Uint8Array(arrayBuffer), true);
    } catch (error) {
        if (error instanceof OverBudgetError) {
            return 'over-budget';
        }
        throw error;
    }

    // `undefined` is a stream that never reached its end marker, i.e. a truncated member.
    if (endStatus !== INFLATE_OK) {
        throw new Error('gzip member could not be inflated');
    }

    const inflated = new Uint8Array(produced);
    let offset = 0;
    for (const chunk of chunks) {
        inflated.set(chunk, offset);
        offset += chunk.length;
    }
    return inflated.buffer as ArrayBuffer;
}
